import { UserMinus, Crown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { NodeAvatar } from '@/components/nodes/NodeAvatar'
import { User } from '@/types'

interface MembersListProps {
  members: User[]
  searchQuery: string
  creatorId: string
  currentUserId: string
  isCreator: boolean
  removingUserId: string | null
  onRemoveMember: (userId: string) => void
  emptyStateMessage?: string
}

export function MembersList({
  members,
  searchQuery,
  creatorId,
  currentUserId: _currentUserId,
  isCreator,
  removingUserId,
  onRemoveMember,
  emptyStateMessage = 'No members yet',
}: MembersListProps) {
  const filteredMembers = members.filter(
    (member) =>
      member.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      member.email?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="space-y-2">
          {filteredMembers.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              {searchQuery ? 'No members found matching your search' : emptyStateMessage}
            </p>
          ) : (
            filteredMembers.map((member) => {
              const isMemberCreator = member.id === creatorId
              const isRemovingThisMember = removingUserId === member.id

              return (
                <div
                  key={member.id}
                  className="flex items-center gap-3 p-3 rounded-lg border hover:bg-muted"
                >
                  <NodeAvatar node={member} size="lg" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{member.name || 'Unknown User'}</p>
                      {isMemberCreator && (
                        <div className="flex items-center gap-1 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                          <Crown className="h-3 w-3" />
                          Creator
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{member.email}</p>
                    <p className="text-xs text-muted-foreground mt-1">{member.role}</p>
                  </div>
                  {isCreator && !isMemberCreator && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onRemoveMember(member.id)}
                      disabled={isRemovingThisMember}
                    >
                      <UserMinus className="h-4 w-4 mr-2" />
                      {isRemovingThisMember ? 'Removing...' : 'Remove'}
                    </Button>
                  )}
                </div>
              )
            })
          )}
        </div>
      </CardContent>
    </Card>
  )
}
